import type { ReactNode } from 'react';

interface SectionMarkProps {
  index?: string;
  count?: number | null;
  countLabel?: string;
  children: ReactNode;
  description?: string;
}

/**
 * Marcador de seção com decoração funcional.
 *
 * - Linha de marca-texto saffron 4px à esquerda do título (ornamento que
 *   reforça a metáfora "alguém revisando texto").
 * - Index discreto em mono à direita (ex: §2 / §3) para navegação.
 * - Count opcional (ex: "5 itens").
 * - Description opcional em sans (peso normal).
 *
 * Princípio E5 (docs/01 §6): decoração FUNCIONAL — cada elemento sinaliza
 * estrutura ou hierarquia, nunca puramente decorativo.
 */
export function SectionMark({ index, count, countLabel, children, description }: SectionMarkProps) {
  return (
    <header className="mb-6">
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <span
            aria-hidden
            className="h-5 w-1 translate-y-[3px] bg-[var(--accent-saffron)]"
          />
          <h2 className="font-serif text-2xl font-medium leading-tight text-[var(--ink-primary)]">
            {children}
          </h2>
        </div>
        {index && (
          <span className="font-mono text-xs uppercase tracking-wider text-[var(--ink-tertiary)]">
            {index}
          </span>
        )}
      </div>
      {(count !== undefined && count !== null) || description ? (
        <p className="mt-2 pl-4 font-sans text-sm text-[var(--ink-tertiary)]">
          {count !== undefined && count !== null && (
            <>
              <span className="font-mono tabular-nums text-[var(--ink-secondary)]">{count}</span>
              {countLabel ? ` ${countLabel}` : ''}
              {description ? ' · ' : ''}
            </>
          )}
          {description}
        </p>
      ) : null}
    </header>
  );
}
